// Comptage des compteurs de quête à partir des events d'un tick, côté CLIENT et HORS
// simulation : les events sont DÉJÀ émis par le moteur (shared/sim.ts), on ne fait que les
// LIRE pour tenir un cumul local (golds/digs/dashes/bounces/monksTrapped) qu'on enverra au
// serveur en fin de partie (POST /api/quests/progress). Rien ici n'entre dans le hash d'état.
//
// Fonction PURE : elle ne mute pas l'entrée, elle renvoie une COPIE incrémentée. Le mapping
// suit l'union GameEvent (shared/state.ts) : `gold` porte `by` (runner|monk) mais pas d'index
// de coureur (en solo il n'y a qu'un coureur) ; `digAnim`/`dash`/`bounce` portent l'index du
// coureur, filtré sur le joueur LOCAL ; `monkTrapped` n'a pas d'auteur (tout piège compte).
import type { QuestCounters } from '../shared/economy'
import type { GameEvent } from '../shared/state'

/** Renvoie une COPIE de `counters` incrémentée selon les events du joueur `localIdx`. Pure.
 *  ⚠️ Le COMBO de moines n'est plus compté ici depuis le 05/08 : il vit dans `avanceCombo`
 *  (fenêtre d'une seconde), tenue par l'appelant — deux moines dans le même 1/30 s n'arrivaient
 *  jamais quand on le cherchait exprès. */
export function tallyEvents(counters: QuestCounters, events: GameEvent[], localIdx: number): QuestCounters {
  const out: QuestCounters = { ...counters }
  for (const e of events) {
    switch (e.type) {
      case 'gold': if (e.by === 'runner') out.golds += 1; break                 // or ramassé PAR un coureur (pas un moine)
      case 'digAnim': if (e.runner === localIdx) out.digs += 1; break           // coup de pioche du joueur local
      case 'dash': if (e.runner === localIdx) out.dashes += 1; break            // dash du joueur local
      case 'bounce': if (e.runner === localIdx) out.bounces += 1; break         // rebond trampoline du joueur local
      case 'monkTrapped': out.monksTrapped += 1; break                          // moine piégé dans un trou (sans auteur)
      // --- Vague 1 (20/07) : compteurs branchés sur des events DÉJÀ émis (AUCUN nouveau GameEvent,
      // hash sim intact). Ceux sans champ `runner` (bombe posée, caisse cassée, télé, clé, porte,
      // moine tué, tazer, objet, poisson) n'ont pas d'auteur dans l'union -> tout compte (en solo il
      // n'y a qu'un coureur ; les rares déclenchements par un moine armé sont assumés faute
      // d'attribution). dashCrystal porte un `runner` -> filtré sur le joueur local, comme dash/bounce.
      case 'bombPlaced': out.bombsPlaced += 1; break                            // bombe posée (objet bombe)
      case 'crateBreak': out.cratesBroken += 1; break                           // caisse bois cassée en tombant
      case 'teleport': out.teleports += 1; break                               // téléporteur emprunté (from/to = cases)
      case 'keyPicked': out.keysPicked += 1; break                             // clé ramassée
      case 'doorOpen': out.doorsOpened += 1; break                             // porte ouverte à la clé
      case 'monkDied': out.monksKilled += 1; break                             // moine éliminé (bombe/tazer/...)
      case 'taze': out.tazes += 1; break                                       // coup de tazer tiré (from/to = cases)
      case 'itemPicked': out.itemsPicked += 1; break                           // objet ramassé (kind)
      case 'fishDied': out.fishBlown += 1; break                               // poisson soufflé par une bombe
      case 'dashCrystal': if (e.runner === localIdx) out.dashCrystals += 1; break // cristal de dash traversé par le joueur local
      default: break                                                            // tout autre event : hors quêtes
    }
  }
  return out
}

// --- LE COMBO DE MOINES (revu le 05/08) --------------------------------------------------------
// Avant : « >= 2 moines piégés dans le MÊME lot d'events d'un tick ». Littéralement le même 1/30 s :
// mesurable en test, introuvable en jeu — deux moines ne tombent pas dans deux trous à la même
// trentième de seconde quand un enfant essaie de le faire exprès. La fenêtre passe à UNE SECONDE.
//
// L'état de fenêtre vit chez l'APPELANT (client/solo.ts) et non en variable de module : la fonction
// reste pure, testable tick par tick, et une nouvelle partie repart de FENETRE_VIERGE sans piège.

export const FENETRE_COMBO_TICKS = 30      // 1 s à 30 Hz
export interface FenetreCombo { debutTick: number; pieges: number }
export const FENETRE_VIERGE: FenetreCombo = { debutTick: 0, pieges: 0 }

/**
 * Fait avancer la fenêtre de combo : `nouveaux` moines piégés au tick `tick`.
 * Renvoie la fenêtre à jour et le nombre de combos complétés (0 ou 1) :
 * dès que 2 pièges tombent à moins d'une seconde d'écart, UN combo, et la fenêtre repart —
 * trois moines d'un coup valent donc un seul combo, comme avant.
 */
export function avanceCombo(fen: FenetreCombo, nouveaux: number, tick: number): { fen: FenetreCombo; combos: number } {
  if (nouveaux <= 0) return { fen, combos: 0 }
  let { debutTick, pieges } = fen
  if (pieges === 0 || tick - debutTick > FENETRE_COMBO_TICKS) { debutTick = tick; pieges = 0 }
  pieges += nouveaux
  if (pieges >= 2) return { fen: { debutTick: tick, pieges: 0 }, combos: 1 }
  return { fen: { debutTick, pieges }, combos: 0 }
}
